import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";

import { AuthProvider } from "@/context/AuthContext";
import PublicLayout from "@/components/layout/PublicLayout";
import AppLayout from "@/components/layout/AppLayout";
import AdminLayout from "@/components/layout/AdminLayout";

// Public
import HomePage from "@/pages/public/HomePage";
import ModulesPage from "@/pages/public/ModulesPage";
import PricingPage from "@/components/pricing/PricingPage";
import StaticCmsPage from "@/pages/public/StaticCmsPage";
import ContactPage from "@/pages/public/ContactPage";

// Auth
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ForgotUsernamePage from "@/pages/auth/ForgotUsernamePage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";

// User
import UserDashboardPage from "@/pages/user/UserDashboardPage";
import JobListPage from "@/pages/user/JobListPage";
import JobCreatePage from "@/pages/user/JobCreatePage";
import JobDetailPage from "@/pages/user/JobDetailPage";
import ReportsPage from "@/pages/user/ReportsPage";
import CreditsPage from "@/pages/user/CreditsPage";
import BillingPage from "@/pages/user/BillingPage";
import NotificationsPage from "@/pages/user/NotificationsPage";
import ProfilePage from "@/pages/user/ProfilePage";
import SettingsPage from "@/pages/user/SettingsPage";
import SampleReportsPage from "@/pages/user/SampleReportsPage";
import SampleReportViewerPage from "@/pages/user/SampleReportViewerPage";

// Admin
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminJobsPage from "@/pages/admin/AdminJobsPage";
import AdminPlansPage from "@/pages/admin/AdminPlansPage";
import AdminModulesPage from "@/pages/admin/AdminModulesPage";
import AdminCmsPage from "@/pages/admin/AdminCmsPage";
import AdminAuditPage from "@/pages/admin/AdminAuditPage";
import AdminMessagesPage from "@/pages/admin/AdminMessagesPage";
import AdminReportsPage from "@/pages/admin/AdminReportsPage";
import AdminCreditLedgerPage from "@/pages/admin/AdminCreditLedgerPage";
import CmsLoginPage from "@/pages/auth/CmsLoginPage";

import ScrollToTop from "@/components/ScrollToTop";

import PrivacyPolicy from "./pages/public/PrivacyPolicy";
import TermsOfService from "./pages/public/TermsOfService";

const APP_URL = process.env.REACT_APP_APP_URL;
const APP_DOMAIN = new URL(APP_URL).hostname;

const MARKETING_PORT = process.env.REACT_APP_MARKETING_PORT;
const APPLICATION_PORT = process.env.REACT_APP_APPLICATION_PORT;

function App() {
  const host = window.location.hostname;
  const port = window.location.port;

  const isDevelopment = process.env.NODE_ENV === "development";

  const isApplicationDomain = isDevelopment
    ? port === APPLICATION_PORT
    : host === APP_DOMAIN;

  const isMarketingDomain = !isApplicationDomain;


  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");

    if (window.location.hostname === "bioinfo.bionivid.in") {
      document.title = "Bionivid BIOINFO | Bioinformatics Platform";
      favicon.href = "/favicon-bionivid.png";
    } else {
      document.title = "SQIT.ai™ | Bioinformatics Analysis Platform";
      favicon.href = "/favicon-sqit.png";
    }
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Marketing */}
          {isMarketingDomain && (
            <>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<StaticCmsPage slug="about" />} />
                <Route path="/modules" element={<ModulesPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/documentation" element={<StaticCmsPage slug="documentation" />} />
                <Route path="/support" element={<StaticCmsPage slug="support" />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route
                  path="/sample-reports"
                  element={<SampleReportsPage />}
                />

                <Route
                  path="/sample-reports/:module"
                  element={<SampleReportViewerPage />}
                />
              </Route>

              {/* CMS */}
              <Route path="/admin" element={<CmsLoginPage />} />
              <Route path="/admin/cms" element={<AdminCmsPage />} />

              {/* Marketing 404 */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}

          {/* Auth (no layout wrapper) */}
          {isApplicationDomain && (
            <>
              <Route
                path="/"
                element={<Navigate to="/login" replace />}
              />

              {/* Auth */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/forgot-username" element={<ForgotUsernamePage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* User portal */}
              <Route path="/app" element={<AppLayout />}>
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<UserDashboardPage />} />
                <Route path="jobs" element={<JobListPage />} />
                <Route path="jobs/new" element={<JobCreatePage />} />
                <Route path="jobs/:id" element={<JobDetailPage />} />
                <Route path="sample-reports" element={<SampleReportsPage />} />
                <Route path="sample-reports/:module" element={<SampleReportViewerPage />} />
                <Route path="credits" element={<CreditsPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>

              {/* Admin portal */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboardPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="jobs" element={<AdminJobsPage />} />
                <Route path="workstation" element={<AdminJobsPage workstation />} />
                <Route path="plans" element={<AdminPlansPage />} />
                <Route path="modules" element={<AdminModulesPage />} />
                <Route path="cms" element={<AdminCmsPage />} />
                <Route path="reports" element={<AdminReportsPage />} />
                <Route path="credit-ledger" element={<AdminCreditLedgerPage />} />
                <Route path="audit" element={<AdminAuditPage />} />
                <Route path="messages" element={<AdminMessagesPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>

              {/* Application 404 */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          )}

        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
